<template>
  <div class="package-manager">
     <div class="text-left py-3">
    <div class="d-flex flex-wrap justify-content-between align-items-center mb-4">
      <h4 class="mr-2">Subscription Packages</h4>
      <div class="btn-group">
        <a href="/subscriptions" class="btn btn-outline-primary">Subscriptions</a>
          <button class="btn btn-primary" @click="showCreateModal">
        <i class="fas fa-plus"></i> New Package
      </button>
      </div>

      </div>
    </div>

    <b-card>
      <b-table
          stacked="md"
        :items="packages"
        :fields="fields"
        :busy="loading"
        responsive
        striped
        hover
      >
        <template #cell(product)="data">
          {{ data.item.product ? data.item.product.name : 'N/A' }}
        </template>

        <template #cell(price)="data">
          KES {{ data.item.price}}
        </template>

        <template #cell(duration)="data">
          {{ data.item.duration_value }} {{ data.item.duration_unit }}(s)
        </template>

        <template #cell(is_active)="data">
          <b-badge :variant="data.item.is_active ? 'success' : 'secondary'">
            {{ data.item.is_active ? 'Active' : 'Inactive' }}
          </b-badge>
        </template>

        <template #cell(actions)="data">
          <b-button size="sm" variant="info" @click="editPackage(data.item)" class="mr-1">
            Edit
          </b-button>
          <b-button
            size="sm"
            :variant="data.item.is_active ? 'warning' : 'success'"
            @click="toggleStatus(data.item)"
          >
            {{ data.item.is_active ? 'Disable' : 'Enable' }}
          </b-button>
        </template>
      </b-table>
    </b-card>

    <!-- Package Modal -->
    <b-modal
      id="package-modal"
      ref="modal"
      :title="editMode ? 'Edit Package' : 'Create Package'"
      @ok="handleSubmit"
      @hidden="resetForm"
    >
      <b-form>
        <b-form-group label="Name *">
          <b-form-input v-model="form.name" required />
        </b-form-group>

        <b-form-group label="Product">
          <b-form-select v-model="form.product_id" :options="productOptions" />
        </b-form-group>

        <b-row>
          <b-col md="6">
            <b-form-group label="Price *">
              <b-form-input v-model="form.price" type="number" step="0.01" required />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Duration Value *">
              <b-form-input v-model="form.duration_value" type="number" required />
            </b-form-group>
          </b-col>
        </b-row>

        <b-form-group label="Duration Unit *">
          <b-form-select v-model="form.duration_unit" :options="durationUnits" required />
        </b-form-group>

        <b-form-group label="Description">
          <b-form-textarea v-model="form.description" rows="3" />
        </b-form-group>

        <b-form-group>
          <b-form-checkbox v-model="form.is_active">
            Active
          </b-form-checkbox>
        </b-form-group>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
export default {
  name: 'PackageManager',

  data() {
    return {
      packages: [],
      loading: false,
      editMode: false,
      currentPackage: null,
      form: {
        name: null,
        product_id: null,
        price: null,
        duration_value: null,
        duration_unit: 'month',
        description: null,
        is_active: true
      },
      productOptions: [],
      durationUnits: [
        { value: 'day', text: 'Day' },
        { value: 'week', text: 'Week' },
        { value: 'month', text: 'Month' },
        { value: 'year', text: 'Year' }
      ],
      fields: [
        { key: 'name', label: 'Name', sortable: true },
        { key: 'product', label: 'Product' },
        { key: 'price', label: 'Price', sortable: true },
        { key: 'duration', label: 'Duration' },
        { key: 'is_active', label: 'Status' },
        { key: 'actions', label: 'Actions' }
      ]
    }
  },

  mounted() {
    this.loadPackages();
    this.loadProducts();
  },

  methods: {
    async loadPackages() {
      this.loading = true;
      try {
        const response = await axios.get('/api/subscription-packages');
        this.packages = response.data;
      } catch (error) {
        this.$bvToast.toast('Failed to load packages', {
          title: 'Error',
          variant: 'danger'
        });
      } finally {
        this.loading = false;
      }
    },

    async loadProducts() {
      try {
        const response = await axios.get('/api/products');
        this.productOptions = [
          { value: null, text: 'None' },
          ...response.data.items.map(p => ({ value: p.id, text: p.product }))
        ];
      } catch (error) {
        console.error('Failed to load products', error);
      }
    },

    showCreateModal() {
      this.editMode = false;
      this.$refs.modal.show();
    },

    editPackage(pkg) {
      this.editMode = true;
      this.currentPackage = pkg;
      this.form = {
        name: pkg.name,
        product_id: pkg.product_id,
        price: pkg.price,
        duration_value: pkg.duration_value,
        duration_unit: pkg.duration_unit,
        description: pkg.description,
        is_active: pkg.is_active
      };
      this.$refs.modal.show();
    },

    async handleSubmit(event) {
      event.preventDefault();

      try {
        if (this.editMode) {
          await axios.put(`/api/subscription-packages/${this.currentPackage.id}`, this.form);
          this.$bvToast.toast('Package updated successfully', {
            title: 'Success',
            variant: 'success'
          });
        } else {
          await axios.post('/api/subscription-packages', this.form);
          this.$bvToast.toast('Package created successfully', {
            title: 'Success',
            variant: 'success'
          });
        }

        this.loadPackages();
        this.$refs.modal.hide();
      } catch (error) {
        this.$bvToast.toast(error.response?.data?.message || 'Failed to save package', {
          title: 'Error',
          variant: 'danger'
        });
      }
    },

    async toggleStatus(pkg) {
      try {
        await axios.put(`/api/subscription-packages/${pkg.id}`, {
          is_active: !pkg.is_active
        });

        this.$bvToast.toast(`Package ${pkg.is_active ? 'disabled' : 'enabled'} successfully`, {
          title: 'Success',
          variant: 'success'
        });

        this.loadPackages();
      } catch (error) {
        this.$bvToast.toast('Failed to update package status', {
          title: 'Error',
          variant: 'danger'
        });
      }
    },

    resetForm() {
      this.editMode = false;
      this.currentPackage = null;
      this.form = {
        name: null,
        product_id: null,
        price: null,
        duration_value: null,
        duration_unit: 'month',
        description: null,
        is_active: true
      };
    }
  }
}
</script>
